import * as React from "react";
import {useMemo, useState} from "react";
import {IPlanDrilldownResponse} from "../../../../api/AdminAnalyticsApi";
import {applySort, SortDir} from "../../../../utils/listUtil";
import {numberWithCommas} from "../../../../utils/numberUtil";

interface IProps {
    data: IPlanDrilldownResponse | null;
    loading: boolean;
    colSpan: number;
    showUserColumns?: boolean;
    loadingMessage?: string;
}

const SortIcon: React.FC<{col: string; active: string; dir: SortDir}> = ({col, active, dir}) => {
    const visible = col === active;
    return (
        <i
            className={`fa-light fa-chevron-${dir === "asc" ? "up" : "down"} admin-analytics__sort-icon`}
            aria-hidden="true"
            style={{visibility: visible ? "visible" : "hidden"}}
        />
    );
};

export const PlanDrilldownTable: React.FC<IProps> = ({
    data,
    loading,
                                                         showUserColumns = false,
    loadingMessage = "Loading details...",
}) => {
    const [sortState, setSortState] = useState<{col: string; dir: SortDir}>({col: "totalHits", dir: "desc"});

    const sortedPages = useMemo(
        () => (data ? applySort(data.pages, sortState.col, sortState.dir) : []),
        [data, sortState],
    );

    const sortTh = (col: string, label: string, extraClass?: string, defaultDir: SortDir = "desc") => {
        const isActive = sortState.col === col;
        const handleClick = () => setSortState({col, dir: isActive ? (sortState.dir === "desc" ? "asc" : "desc") : defaultDir});
        const handleKey = (e: React.KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleClick(); }
        };
        return (
            <th
                className={`admin-analytics__th--sortable${extraClass ? ` ${extraClass}` : ""}`}
                onClick={handleClick}
                onKeyDown={handleKey}
                tabIndex={0}
                role="columnheader"
                aria-sort={isActive ? (sortState.dir === "asc" ? "ascending" : "descending") : undefined}
            >
                {label} <SortIcon col={col} active={sortState.col} dir={sortState.dir} />
            </th>
        );
    };

    if (loading) {
        return (
            <div className="admin-analytics__drilldown-loading">
                <span className="admin-analytics__inline-spinner" /> {loadingMessage}
            </div>
        );
    }

    if (!data) {
        return <div className="admin-analytics__drilldown-empty">Could not load drilldown data.</div>;
    }

    if (data.pages.length === 0) {
        return <div className="admin-analytics__drilldown-empty">No page-level data available for this plan.</div>;
    }

    return (
        <div className="admin-analytics__drilldown">
            <div className="admin-analytics__drilldown-summary">
                <span><strong>{numberWithCommas(data.uniqueUsers)}</strong> unique users</span>
                <span><strong>{numberWithCommas(data.totalPageViews)}</strong> total page views</span>
                <span><strong>{data.pages.length}</strong> pages visited</span>
            </div>
            <table className="admin-analytics__table admin-analytics__table--nested">
                <thead>
                    <tr>
                        {sortTh("page", "Page", undefined, "asc")}
                        {sortTh("totalHits", "Total Hits", "admin-analytics__cell--right")}
                        {sortTh("internalHits", "Internal Hits", "admin-analytics__cell--right")}
                        {sortTh("clientHits", "Client Hits", "admin-analytics__cell--right")}
                        {showUserColumns && sortTh("uniqueInternalUsers", "Internal Users", "admin-analytics__cell--right")}
                        {showUserColumns && sortTh("uniqueClientUsers", "Client Users", "admin-analytics__cell--right")}
                    </tr>
                </thead>
                <tbody>
                    {sortedPages.map((p) => (
                        <tr key={p.page}>
                            <td>{p.page}</td>
                            <td className="admin-analytics__cell--right">{numberWithCommas(p.totalHits)}</td>
                            <td className="admin-analytics__cell--right">{numberWithCommas(p.internalHits)}</td>
                            <td className="admin-analytics__cell--right">{numberWithCommas(p.clientHits)}</td>
                            {showUserColumns &&
                                <td className="admin-analytics__cell--right">
                                    {numberWithCommas(p.uniqueInternalUsers)}
                                </td>}
                            {showUserColumns &&
                                <td className="admin-analytics__cell--right">
                                    {numberWithCommas(p.uniqueClientUsers)}
                                </td>}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
